import {
  type ArdoqId,
  type FilterInterfaceFilter,
  type FormattingFilter,
  type GroupingRule,
  ViewIds,
} from '@ardoq/api-types';
import { isTruthy } from '@ardoq/common-helpers';
import { componentOperations } from '@ardoq/core';
import { EnhancedScopeData, GlobalReferenceTypeId } from '@ardoq/data-model';
import { componentUtils, type FilterColorData } from '@ardoq/scope-data';
import { vizFilterGray } from '@ardoq/design-tokens';
import {
  hasDefaultGreyColorComponentFilter,
  hasDefaultGreyColorReferenceFilter,
  maybeGrayOutTypes,
} from 'models/utils/filterUtils';
import {
  buildNodes,
  buildNodesWithHierarchy,
  collapseNodes,
  consolidateNodeReferenceParentsReducer,
  createReferenceTypeNamesMap,
  DependencyMapNode,
  type DependencyMapViewModel,
  getMaxTreeDepth,
  getNodeAddressMap,
  getReferenceChildMaxDepth,
  getReferenceParentMaxDepthReducer,
  hasNonComponentNodes,
  LabelsData,
  limitNodes,
  nodeFromComponentId,
  nodeReferenceTypeIdToLegendReferenceType,
  nodesComponentIds,
  nodesReferenceIds,
  ReferenceListReference,
  sortNodes,
} from '@ardoq/dependency-map';
import { filterClass } from '@ardoq/color-helpers';
import { ItemsType, nodeLimitError } from '@ardoq/error-info-box';
import { DEPENDENCY_MAP_ITEMS_LIMIT } from 'views/environmentals/viewDataLimits';
import { getNodeLimitErrorLabels } from 'locales/getErrorInfoBoxLabels';
import { CommonDropdownOptions } from '@ardoq/global-consts';
import { Features, hasFeature } from '@ardoq/features';
import {
  buildGraphFromTraversalAndSearch,
  getCanBeUsedAsGroup,
  getComponentTypesInfo,
  getGraphEdgeLabelForViewpointMode,
  getGraphEdgeLabelForWorkspaceMode,
  getGraphInterfaceWithModelInterfaces,
  getGraphNodeOrGroupLabelForViewpointMode,
  GetLabelsDataInterface,
  getLabelsInterfaceWithModelInterfaces,
  getNodeMainLabel,
  getOtherLabelsForComponent,
  getReferenceTypeDetails,
  ItemLabels,
  LabelsInterface,
  RawGraphEdge2,
  RawGraphNode2,
  reduceLegendReferenceTypes,
  TraversalViewModel,
  pruneToStartSetBranches,
} from '@ardoq/graph';
import { ReferenceFilterContext } from '@ardoq/filter-interface';
import { dispatchAction } from '@ardoq/rxbeach';
import { uniq } from 'lodash';
import { buildGraph } from 'modelInterface/graph/buildGraphForWorkspaceMode';
import { resolveEdgeReferenceDisplayLabels } from 'modelInterface/graph/resolveEdgeReferenceDisplayLabel';
import { getGraphShapeForTraversalHierarchy } from 'modelInterface/graph/graphShapeForTraversalHierarchy';
import { urlFieldValues } from 'tabview/graphViews/urlFieldValues';
import { getStartSetForWorkspaceMode } from 'tabview/graphViews/util';
import { startPathsAfterParentChildHops } from 'traversals/startPathsAfterParentChildHops';
import { trackEvent } from 'tracking/tracking';
import { formatNumber } from '@ardoq/locale';
import { bypassLimit as bypassLimitAction } from './actions';
import { applyUniqueIds } from './applyUniqueIds';
import { buildComponentItems as buildComponentRawGraphItems } from './buildViewModelHelpers';
import { createCleanNode } from './cleanNode';
import { dependencyMapTraversalViewModel } from './dependencyMapTraversalViewModel';
import { getViewSettingsTraverseConfig } from './traverseAndGrouping';
import { shouldStartPathsAfterParentChildHops } from './shouldStartPathsAfterParentChildHops';
import {
  BuildViewModelArgs,
  BuildViewpointModeViewModelArgs,
  DependencyMapGroupingData,
  GroupingResult,
  ViewpointTraversalHierarchyGroupingResult,
} from './types';
import type { I18nLocale } from '@ardoq/i18n';
import { getDependencyMapGroupingData, getDirectReferenceNodes } from './util';

const VIEW_ID = ViewIds.DEPENDENCY_MAP_2;

const buildReferenceList = (
  graphReferenceList: RawGraphEdge2[],
  globalTypeIdByReferenceId: ReadonlyMap<string, GlobalReferenceTypeId | null>
): ReferenceListReference[] =>
  graphReferenceList
    .filter(reference => !reference.isParentChildReference)
    .map(reference => ({
      ...reference,
      referenceTypeId:
        (reference.modelId &&
          globalTypeIdByReferenceId.get(reference.modelId)) ??
        null,
    }));

const getEdgeLabelsDataForWorkspaceMode = (
  referenceList: ReferenceListReference[],
  resolvedEdgeReferenceDisplayLabels: ReadonlyMap<string, string | null>,
  i18nLocale: I18nLocale,
  isShowReferenceTypeApplied: boolean
): Map<string, ItemLabels> => {
  return new Map<string, ItemLabels>(
    referenceList.map(rawGraphItemWithTypeId => [
      rawGraphItemWithTypeId.id,
      getGraphEdgeLabelForWorkspaceMode(
        rawGraphItemWithTypeId,
        resolvedEdgeReferenceDisplayLabels.get(rawGraphItemWithTypeId.id) ??
          null,
        i18nLocale,
        isShowReferenceTypeApplied
      ),
    ])
  );
};

const getEdgeLabelsDataForViewpointMode = (
  labelsDataInterface: LabelsInterface,
  referenceList: ReferenceListReference[],
  isShowReferenceTypeApplied: boolean,
  i18nLocale: I18nLocale
): Map<string, ItemLabels> => {
  return new Map<string, ItemLabels>(
    referenceList.map(rawGraphItemWithTypeId => [
      rawGraphItemWithTypeId.id,
      getGraphEdgeLabelForViewpointMode(
        labelsDataInterface,
        rawGraphItemWithTypeId,
        i18nLocale,
        isShowReferenceTypeApplied
      ),
    ])
  );
};

const EMPTY_TRAVERSAL_VIEW_MODEL: TraversalViewModel = {
  traversedOutgoingReferenceTypes: [],
  traversedIncomingReferenceTypes: [],
  referenceTypes: [],
};

function* ancestors(
  scopeData: EnhancedScopeData,
  componentId: ArdoqId,
  maxDepth: number,
  currentDepth = 0
): Generator<ArdoqId> {
  if (currentDepth >= maxDepth) return;

  const parentId = componentUtils.getParentId(scopeData, componentId);
  if (!parentId) return;

  yield parentId;

  yield* ancestors(scopeData, parentId, maxDepth, currentDepth + 1);
}

function* descendants(
  scopeData: EnhancedScopeData,
  componentId: ArdoqId,
  maxDepth: number,
  currentDepth = 0
): Generator<ArdoqId> {
  if (currentDepth >= maxDepth) return;

  const children = componentUtils.getChildrenIds(scopeData, componentId);
  if (!children?.length) return;

  for (const childId of children) {
    yield childId;

    yield* descendants(scopeData, childId, maxDepth, currentDepth + 1);
  }
}

const withRelatedComponents = (
  scopeData: EnhancedScopeData,
  allComponentIds: ArdoqId[],
  viewSettingsDegreesOfChildhood: number,
  viewSettingsDegreesOfParenthood: number
): ArdoqId[] => {
  const allRelated = new Set<ArdoqId>();

  for (const componentId of allComponentIds) {
    allRelated.add(componentId);

    const descendantIds = descendants(
      scopeData,
      componentId,
      viewSettingsDegreesOfChildhood
    );
    const ancestorIds = ancestors(
      scopeData,
      componentId,
      viewSettingsDegreesOfParenthood
    );

    for (const id of descendantIds) {
      allRelated.add(id);
    }
    for (const id of ancestorIds) {
      allRelated.add(id);
    }
  }

  return Array.from(allRelated);
};

const getNodesLabelDataForWorkspaceMode = (
  scopeData: EnhancedScopeData,
  componentRawGraphItems: Map<string, RawGraphNode2>,
  groupMap: Map<string, RawGraphNode2>
): Map<string, ItemLabels> => {
  const nodeLabelsData = new Map<string, ItemLabels>();

  for (const componentItem of componentRawGraphItems.values()) {
    nodeLabelsData.set(componentItem.id, {
      mainLabel: getNodeMainLabel(componentItem, true),
      subLabel: componentItem.subLabels?.join(', '),
    });
  }

  for (const groupItem of groupMap.values()) {
    const modelId = groupItem.modelId ?? groupItem.id;
    const isComponentNode = componentUtils.isComponent(scopeData, modelId);
    nodeLabelsData.set(groupItem.id, {
      mainLabel: getNodeMainLabel(groupItem, isComponentNode),
      subLabel: groupItem.subLabels?.join(', '),
    });
  }

  return nodeLabelsData;
};

const getNodesLabelDataForViewpointMode = (
  labelsDataInterface: GetLabelsDataInterface,
  componentRawGraphItems: Map<string, RawGraphNode2>,
  groupMap: Map<string, RawGraphNode2>,
  i18nLocale: I18nLocale
): Map<string, ItemLabels> => {
  const nodeLabelsData = new Map<string, ItemLabels>();

  for (const componentItem of componentRawGraphItems.values()) {
    nodeLabelsData.set(
      componentItem.id,
      getGraphNodeOrGroupLabelForViewpointMode(
        labelsDataInterface,
        componentItem,
        i18nLocale
      )
    );
  }

  for (const groupItem of groupMap.values()) {
    nodeLabelsData.set(
      groupItem.id,
      getGraphNodeOrGroupLabelForViewpointMode(
        labelsDataInterface,
        groupItem,
        i18nLocale
      )
    );
  }

  return nodeLabelsData;
};

/** @returns a {@link LabelsData} with the labels for one component and no references. */
const singleComponentLabelData = (
  modelId: ArdoqId,
  mainLabel: string | null,
  i18nLocale: I18nLocale
): LabelsData => ({
  nodeLabelsData: new Map([
    [
      modelId,
      {
        mainLabel,
        otherLabels: getOtherLabelsForComponent(
          getLabelsInterfaceWithModelInterfaces(),
          modelId,
          i18nLocale
        ),
      },
    ],
  ]),
  edgeLabelsData: new Map(),
});
/** @returns a {@link DependencyMapNode} from the given component ID. */
const componentNode = (
  modelId: ArdoqId,
  mainLabel: string | null,
  scopeData: EnhancedScopeData,
  filterColorData: FilterColorData,
  i18nLocale: I18nLocale
): DependencyMapNode => ({
  ...nodeFromComponentId(
    modelId,
    singleComponentLabelData(modelId, mainLabel, i18nLocale),
    undefined,
    scopeData,
    filterColorData
  ),
  id: modelId,
});

export type DependencyMapViewInterface = ReturnType<
  typeof getGraphInterfaceWithModelInterfaces
> &
  ReturnType<typeof getLabelsInterfaceWithModelInterfaces>;

type ModeSpecificConfig = {
  groupingData: DependencyMapGroupingData;
  viewInterface: DependencyMapViewInterface;
  traverseAndGroupingResult:
    | GroupingResult
    | ViewpointTraversalHierarchyGroupingResult;
};

const sortAndConsolidateNodes = (
  nodesToRender: DependencyMapNode[],
  sort: BuildViewModelArgs['context']['sort'],
  locale: BuildViewModelArgs['locale'],
  scopeData: EnhancedScopeData,
  /** the context workspace IDs. When "Order" is the context sort, these are needed in order for the Dependency Map to match the navigator sort in `compareComponentIds`.  */
  workspaceIds: BuildViewModelArgs['context']['workspaceIds']
) =>
  sortNodes(nodesToRender, sort, locale, scopeData, workspaceIds).reduce(
    consolidateNodeReferenceParentsReducer,
    []
  );

const buildViewModelCommon = (
  { viewSettings, i18nLocale, locale }: BuildViewModelArgs,
  bypassLimit: boolean,
  uncollapsedNodes: DependencyMapNode[],
  modeConfig: ModeSpecificConfig,
  scopeData: EnhancedScopeData,
  formattingFilters: (FormattingFilter | FilterInterfaceFilter)[]
) => {
  const graphItemIdsByNodeId = applyUniqueIds(uncollapsedNodes);
  const nodeAddressMap = getNodeAddressMap({
    nodes: uncollapsedNodes,
    groupMap: modeConfig.traverseAndGroupingResult.graph.groupMap,
    graphItemIdsByNodeId,
    getDirectReferenceNodes,
  });
  const treeDepths = uncollapsedNodes.map(getMaxTreeDepth);
  const maxTreeDepth = treeDepths.length > 0 ? Math.max(...treeDepths) : 0;

  const { result: collapsedNodes } = collapseNodes(
    uncollapsedNodes,
    viewSettings.treeDepth,
    nodeAddressMap
  );

  const {
    nodes: nodesToRender,
    itemCount,
    originalItemCount,
  } = bypassLimit
    ? { nodes: collapsedNodes, itemCount: NaN, originalItemCount: NaN }
    : limitNodes(collapsedNodes, DEPENDENCY_MAP_ITEMS_LIMIT);
  const componentIds = uniq(Array.from(nodesComponentIds(nodesToRender)));
  const { componentTypes, legendComponentTypes } =
    getComponentTypesInfo(componentIds);

  const referenceTypeIdToLegendType = nodeReferenceTypeIdToLegendReferenceType(
    formattingFilters,
    scopeData
  );
  const referenceIds = uniq(Array.from(nodesReferenceIds(nodesToRender)));
  const referenceTypes = getReferenceTypeDetails(referenceIds);
  const legendReferenceTypes = viewSettings.colorByReference
    ? referenceTypes
        .map(({ referenceType }) =>
          referenceTypeIdToLegendType(referenceType?.id ?? '')
        )
        .filter(isTruthy)
        .reduce(reduceLegendReferenceTypes, {
          result: [],
          keyedResults: new Map(),
          considerLineCaps: false,
        }).result
    : [];

  const errors = [
    ...modeConfig.traverseAndGroupingResult.graph.errors,
    itemCount < originalItemCount &&
      nodeLimitError({
        itemCount: originalItemCount,
        formatNumber: value => formatNumber(value, 'default', locale),
        onProceedAnyway: () => {
          trackEvent('Clicked "Proceed anyway" button');
          dispatchAction(bypassLimitAction());
        },
        limit: DEPENDENCY_MAP_ITEMS_LIMIT,
        itemsType: ItemsType.COMPONENTS_AND_REFERENCES,
        ...getNodeLimitErrorLabels(i18nLocale),
      }),
  ].filter(isTruthy);

  const referenceParentMaxDepthReducer =
    getReferenceParentMaxDepthReducer(scopeData);
  const referenceParentMaxDepth = nodesToRender.reduce(
    referenceParentMaxDepthReducer,
    0
  );

  const referenceChildMaxDepth = getReferenceChildMaxDepth(scopeData);

  const childrenDepths = nodesToRender.map(referenceChildMaxDepth);

  const referenceChildrenMaxDepth =
    childrenDepths.length > 0 ? Math.max(...childrenDepths) : 0;

  const { urlFieldValuesByComponentId, urlFieldValuesByReferenceId } =
    urlFieldValues({
      viewInterface: modeConfig.viewInterface,
      componentTypes,
      referenceTypes,
      componentIds,
      referenceIds,
    });

  return {
    rawNodes: nodesToRender,
    componentTypes: legendComponentTypes,
    legendReferenceTypes,
    maxTreeDepth,
    errors,
    itemCount,
    referenceParentMaxDepth,
    referenceChildMaxDepth: referenceChildrenMaxDepth,
    nodeAddressMap,
    hasNonComponentNodes: nodesToRender.some(hasNonComponentNodes),
    urlFieldValuesByComponentId,
    urlFieldValuesByReferenceId,
    formattingFilters,
  };
};

export const buildWorkspaceModeViewModel = (
  buildViewModelArgs: BuildViewModelArgs,
  bypassLimit: boolean,
  scopeData: EnhancedScopeData,
  filterColorData: FilterColorData,
  viewInterface: DependencyMapViewInterface,
  isShowReferenceTypeApplied: boolean,
  formattingFilters: (FormattingFilter | FilterInterfaceFilter)[],
  groupingRules: GroupingRule[],
  globalTypeIdByReferenceId: ReadonlyMap<string, GlobalReferenceTypeId | null>,
  referenceFilterContext: ReferenceFilterContext,
  viewpointLabelsInterface: DependencyMapViewInterface | null
): DependencyMapViewModel & {
  rawNodes: DependencyMapNode[];
} => {
  const groupingData = getDependencyMapGroupingData({
    loadedScenarioData: buildViewModelArgs.loadedScenarioData,
    showScopeRelated: buildViewModelArgs.showScopeRelated,
    scenarioRelatedComponents: buildViewModelArgs.scenarioRelatedComponents,
    groupingRules,
  });

  const showOnlyConnectedComponents =
    buildViewModelArgs.viewSettings.showOnlyConnectedComponents ?? false;

  const viewSettingsTraverseConfig = getViewSettingsTraverseConfig({
    supportedUserDefinedGroupingRules:
      groupingData.supportedUserDefinedGroupingRules,
    viewSettings: buildViewModelArgs.viewSettings,
    graphModel: buildViewModelArgs.graphModel,
    scenarioRelatedComponentIds: buildViewModelArgs.scenarioRelatedComponentIds,
    degreesOfChildhood: buildViewModelArgs.viewSettings.degreesOfChildhood,
  });

  const startSet = getStartSetForWorkspaceMode(
    buildViewModelArgs.context,
    componentOperations.buildChildrenByParent(scopeData.components)
  );

  const { baseTraverseOptions, referenceTraverseOptions } =
    viewSettingsTraverseConfig;

  // This is a hacky quick-win performance improvement to reduce calls to
  // buildGraph (which can be quite expensive) in presentations. The
  // traversalViewModel is only used to populate the traversal dropdown menu in
  // the app, so it serves no purpose in presentations.
  const traversalViewModel: TraversalViewModel =
    buildViewModelArgs.isPresentationMode
      ? EMPTY_TRAVERSAL_VIEW_MODEL
      : dependencyMapTraversalViewModel({
          startSet,
          graphModel: buildViewModelArgs.graphModel,
          baseTraverseOptions,
          referenceTraverseOptions,
          scopeData,
          i18nLocale: buildViewModelArgs.i18nLocale,
          referenceFilterContext,
        });

  // In workspace mode, we always use buildGraph directly
  const graph = buildGraph({
    graphInterface: viewInterface,
    viewId: VIEW_ID,
    startSet,
    graph: buildViewModelArgs.graphModel,
    traverseOptions: viewSettingsTraverseConfig.baseTraverseOptions,
    sequentialTraverseOptions:
      viewSettingsTraverseConfig.sequentialTraverseOptions,
    groupingRules: groupingData.appliedGroupingRules,
    includeOnlyConnectedComponents: showOnlyConnectedComponents,
    useNewGrouping: buildViewModelArgs.viewSettings.useNewGrouping,
    i18nLocale: buildViewModelArgs.i18nLocale,
    ctx: referenceFilterContext,
  });

  const traverseAndGroupingResult = {
    graph,
    startSet,
  };

  const referenceList = buildReferenceList(
    traverseAndGroupingResult.graph.referenceList,
    globalTypeIdByReferenceId
  );

  const resolvedEdgeReferenceDisplayLabels =
    resolveEdgeReferenceDisplayLabels(referenceList);

  const componentRawGraphItems = buildComponentRawGraphItems({
    scopeData,
    componentMap: traverseAndGroupingResult.graph.componentMap,
    groupMap: traverseAndGroupingResult.graph.groupMap,
    degreesOfChildhood: buildViewModelArgs.viewSettings.degreesOfChildhood,
    degreesOfParenthood: buildViewModelArgs.viewSettings.degreesOfParenthood,
    withRelatedComponents: (ids, childhood, parenthood) =>
      withRelatedComponents(scopeData, ids, childhood, parenthood),
  });

  const labelsData: LabelsData = viewpointLabelsInterface
    ? {
        nodeLabelsData: getNodesLabelDataForViewpointMode(
          viewpointLabelsInterface,
          componentRawGraphItems,
          traverseAndGroupingResult.graph.groupMap,
          buildViewModelArgs.i18nLocale
        ),
        edgeLabelsData: getEdgeLabelsDataForViewpointMode(
          viewpointLabelsInterface,
          referenceList,
          isShowReferenceTypeApplied,
          buildViewModelArgs.i18nLocale
        ),
      }
    : {
        nodeLabelsData: getNodesLabelDataForWorkspaceMode(
          scopeData,
          componentRawGraphItems,
          traverseAndGroupingResult.graph.groupMap
        ),
        edgeLabelsData: getEdgeLabelsDataForWorkspaceMode(
          referenceList,
          resolvedEdgeReferenceDisplayLabels,
          buildViewModelArgs.i18nLocale,
          isShowReferenceTypeApplied
        ),
      };

  const referenceTypeNames = createReferenceTypeNamesMap(
    referenceList,
    scopeData
  );

  const uncollapsedNodes = buildNodes({
    ...traverseAndGroupingResult,
    referenceList,
    incomingDegreesOfRelationship:
      buildViewModelArgs.viewSettings.incomingDegreesOfRelationship,
    outgoingDegreesOfRelationship:
      buildViewModelArgs.viewSettings.outgoingDegreesOfRelationship,
    incomingReferenceTypes:
      viewSettingsTraverseConfig.userTraverseOptions?.incomingReferenceTypes ??
      null,
    outgoingReferenceTypes:
      viewSettingsTraverseConfig.userTraverseOptions?.outgoingReferenceTypes ??
      null,
    showOnlyConnectedComponents,
    isUserDefinedGrouping:
      groupingData.supportedUserDefinedGroupingRules.length > 0,
    degreesOfParenthood: buildViewModelArgs.viewSettings.degreesOfParenthood,
    degreesOfChildhood: buildViewModelArgs.viewSettings.degreesOfChildhood,
    contextComponentModelId: buildViewModelArgs.context.componentId,
    cleanNode: createCleanNode(false),
    labelsData,
    referenceTypeNames,
    sort: buildViewModelArgs.context.sort,
    locale: buildViewModelArgs.locale,
    scopeData,
    workspaceIds: buildViewModelArgs.context.workspaceIds,
    filterColorData,
    groupBys: groupingData.appliedGroupingRules,
  });

  const commonResult = buildViewModelCommon(
    buildViewModelArgs,
    bypassLimit,
    uncollapsedNodes,
    {
      groupingData,
      viewInterface,
      traverseAndGroupingResult,
    },
    scopeData,
    formattingFilters
  );

  const noConnectedComponents =
    showOnlyConnectedComponents && commonResult.rawNodes.length === 0;
  const contextComponentIsNotConnected =
    noConnectedComponents && buildViewModelArgs.context.componentId;
  const nodes = contextComponentIsNotConnected
    ? [
        componentNode(
          buildViewModelArgs.context.componentId,
          viewInterface.getComponentDisplayLabel(
            buildViewModelArgs.context.componentId
          ),
          scopeData,
          filterColorData,
          buildViewModelArgs.i18nLocale
        ),
      ]
    : sortAndConsolidateNodes(
        commonResult.rawNodes,
        buildViewModelArgs.context.sort,
        buildViewModelArgs.locale,
        scopeData,
        buildViewModelArgs.context.workspaceIds
      );

  return {
    ...commonResult,
    nodes,
    noConnectedComponents,
    ...traversalViewModel,
    hasClones: traverseAndGroupingResult.graph.hasClones,
    isUserDefinedGrouping:
      groupingData.supportedUserDefinedGroupingRules.length > 0,
  };
};

const toStartSetBranches = (
  groupingResult: ViewpointTraversalHierarchyGroupingResult
): ViewpointTraversalHierarchyGroupingResult => {
  const { graph, rootItems, startSet } = groupingResult;
  const prunedHierarchy = pruneToStartSetBranches({
    rootItems,
    startSet,
    componentMap: graph.componentMap,
    groupMap: graph.groupMap,
  });
  return {
    ...groupingResult,
    rootItems: prunedHierarchy.rootItems,
    graph: {
      ...graph,
      componentMap: prunedHierarchy.componentMap,
      groupMap: prunedHierarchy.groupMap,
    },
  };
};

const toViewpointTraversalHierarchy = (
  groupingResult: ViewpointTraversalHierarchyGroupingResult,
  shouldShapeHierarchyByTraversal: boolean
): ViewpointTraversalHierarchyGroupingResult => {
  if (!shouldShapeHierarchyByTraversal) {
    return groupingResult;
  }
  return toStartSetBranches(groupingResult);
};

export const buildViewpointModeViewModel = (
  buildViewModelArgs: BuildViewpointModeViewModelArgs,
  bypassLimit: boolean,
  scopeData: EnhancedScopeData,
  filterColorData: FilterColorData,
  viewInterface: DependencyMapViewInterface,
  hasReferenceLabels: boolean,
  isShowReferenceTypeApplied: boolean,
  formattingFilters: (FormattingFilter | FilterInterfaceFilter)[],
  groupingData: DependencyMapGroupingData
): DependencyMapViewModel & {
  rawNodes: DependencyMapNode[];
} => {
  const { globalTypeIdByReferenceId } = buildViewModelArgs;
  const shouldUseViewpointTraversalHierarchy =
    !groupingData.hasUserDefinedReferenceTypeGroups;
  const shouldShapeHierarchyByTraversal = hasFeature(
    Features.VISUALIZATIONS_TMP_TRAVERSAL_SHAPED_HIERARCHY
  );
  const loadedGraph = shouldStartPathsAfterParentChildHops(groupingData)
    ? {
        ...buildViewModelArgs.loadedGraph,
        hierarchyDefinition: startPathsAfterParentChildHops(
          buildViewModelArgs.loadedGraph.hierarchyDefinition
        ),
      }
    : buildViewModelArgs.loadedGraph;

  const startSet = uniq(
    loadedGraph.hierarchyDefinition.flatMap(
      ({ startSetResult }) => startSetResult
    )
  );

  // In viewpoint mode, traversalViewModel is always minimal
  const traversalViewModel: TraversalViewModel = EMPTY_TRAVERSAL_VIEW_MODEL;

  const graph = buildGraphFromTraversalAndSearch({
    graphInterface: viewInterface,
    viewId: VIEW_ID,
    graph: buildViewModelArgs.graphModel,
    groupingRules: groupingData.appliedGroupingRules,
    loadedGraph,
    canBeUsedAsGroup: getCanBeUsedAsGroup(
      viewInterface,
      loadedGraph,
      groupingData.supportedUserDefinedGroupingRules.length > 0
    ),
    i18nLocale: buildViewModelArgs.i18nLocale,
  });

  const traverseAndGroupingResult = {
    graph,
    startSet,
    ...(shouldUseViewpointTraversalHierarchy
      ? getGraphShapeForTraversalHierarchy({
          loadedGraph,
          traversalAndGroupingResult: graph,
          startSet,
        })
      : {}),
  };

  const referenceList = buildReferenceList(
    traverseAndGroupingResult.graph.referenceList,
    globalTypeIdByReferenceId
  );

  const componentRawGraphItems = buildComponentRawGraphItems({
    scopeData,
    componentMap: traverseAndGroupingResult.graph.componentMap,
    groupMap: traverseAndGroupingResult.graph.groupMap,
    degreesOfChildhood: buildViewModelArgs.viewSettings.degreesOfChildhood,
    degreesOfParenthood: buildViewModelArgs.viewSettings.degreesOfParenthood,
    withRelatedComponents: (ids, childhood, parenthood) =>
      withRelatedComponents(scopeData, ids, childhood, parenthood),
  });

  const labelsData: LabelsData = {
    nodeLabelsData: getNodesLabelDataForViewpointMode(
      viewInterface,
      componentRawGraphItems,
      traverseAndGroupingResult.graph.groupMap,
      buildViewModelArgs.i18nLocale
    ),
    edgeLabelsData: getEdgeLabelsDataForViewpointMode(
      viewInterface,
      referenceList,
      isShowReferenceTypeApplied,
      buildViewModelArgs.i18nLocale
    ),
  };

  const referenceTypeNames = createReferenceTypeNamesMap(
    referenceList,
    scopeData
  );

  const isNewConditionalFormatting = true;
  const uncollapsedNodes = shouldUseViewpointTraversalHierarchy
    ? buildNodesWithHierarchy({
        ...toViewpointTraversalHierarchy(
          traverseAndGroupingResult as ViewpointTraversalHierarchyGroupingResult,
          shouldShapeHierarchyByTraversal
        ),
        degreesOfParenthood:
          buildViewModelArgs.viewSettings.degreesOfParenthood,
        degreesOfChildhood: buildViewModelArgs.viewSettings.degreesOfChildhood,
        cleanNode: createCleanNode(true),
        labelsData,
        isNewConditionalFormatting,
        referenceTypeNames,
        sort: buildViewModelArgs.context.sort,
        locale: buildViewModelArgs.locale,
        i18nLocale: buildViewModelArgs.i18nLocale,
        scopeData,
        workspaceIds: buildViewModelArgs.context.workspaceIds,
        filterColorData,
        groupBys: groupingData.appliedGroupingRules,
        hasReferenceLabels,
        omitLinksToGroupingAncestors: shouldShapeHierarchyByTraversal,
      })
    : buildNodes({
        ...traverseAndGroupingResult,
        referenceList,
        incomingDegreesOfRelationship: Infinity,
        outgoingDegreesOfRelationship: Infinity,
        incomingReferenceTypes: new Set([CommonDropdownOptions.ALL]),
        outgoingReferenceTypes: new Set([CommonDropdownOptions.ALL]),
        showOnlyConnectedComponents: false,
        isUserDefinedGrouping:
          groupingData.supportedUserDefinedGroupingRules.length > 0,
        degreesOfParenthood:
          buildViewModelArgs.viewSettings.degreesOfParenthood,
        degreesOfChildhood: buildViewModelArgs.viewSettings.degreesOfChildhood,
        contextComponentModelId: buildViewModelArgs.context.componentId,
        cleanNode: createCleanNode(true),
        labelsData,
        isNewConditionalFormatting,
        referenceTypeNames,
        sort: buildViewModelArgs.context.sort,
        locale: buildViewModelArgs.locale,
        scopeData,
        workspaceIds: buildViewModelArgs.context.workspaceIds,
        filterColorData,
        groupBys: groupingData.appliedGroupingRules,
      });

  const commonResult = buildViewModelCommon(
    buildViewModelArgs,
    bypassLimit,
    uncollapsedNodes,
    {
      groupingData,
      viewInterface,
      traverseAndGroupingResult,
    },
    scopeData,
    formattingFilters
  );

  const nodes = sortAndConsolidateNodes(
    commonResult.rawNodes,
    buildViewModelArgs.context.sort,
    buildViewModelArgs.locale,
    scopeData,
    buildViewModelArgs.context.workspaceIds
  );

  // Apply grey color to legend reference types in viewpoint mode when "Keep original color"
  // is unchecked and "Color by reference type" is enabled
  const shouldApplyGreyToReferences =
    buildViewModelArgs.viewSettings.colorByReference &&
    hasDefaultGreyColorReferenceFilter(commonResult.formattingFilters);

  const legendReferenceTypes = shouldApplyGreyToReferences
    ? commonResult.legendReferenceTypes.map(refType => ({
        ...refType,
        color: vizFilterGray,
        className: [refType.className, filterClass(vizFilterGray)]
          .filter(isTruthy)
          .join(' '),
      }))
    : commonResult.legendReferenceTypes;

  // Apply grey color to legend component types in viewpoint mode when "Keep original color"
  // is unchecked and "Color by reference type" is disabled (component types are shown)
  const shouldApplyGreyToComponents =
    !buildViewModelArgs.viewSettings.colorByReference &&
    hasDefaultGreyColorComponentFilter(commonResult.formattingFilters);

  const legendComponentTypes = maybeGrayOutTypes(
    commonResult.componentTypes,
    shouldApplyGreyToComponents,
    'borderColor'
  );

  return {
    ...commonResult,
    componentTypes: legendComponentTypes,
    legendReferenceTypes,
    nodes,
    noConnectedComponents: false,
    ...traversalViewModel,
    hasClones: traverseAndGroupingResult.graph.hasClones,
    isUserDefinedGrouping:
      groupingData.supportedUserDefinedGroupingRules.length > 0,
  };
};
